/**
 * Molecule: ProjectSelector
 *
 * THE canonical project selector for the platform. Always use this instead
 * of rolling your own <select>+projects.list().
 *
 * Dropdown renders via React portal (document.body) so it works inside
 * Modal, SlideOver, and any other overflow:hidden/auto container.
 *
 * Guarantees:
 * - Soft-deleted projects (deleted_at IS NOT NULL) are NEVER shown
 * - Internal projects are excluded by default
 * - Searchable for long project lists
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Click-outside-to-close (portal-aware)
 * - 30s cache TTL — deleted projects disappear within 30 seconds
 *
 * Usage:
 *   <ProjectSelector value={form.project_id} onChange={v => ...} />
 */
import React, { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { tokens } from '@/design-system/tokens'
import { projects as projectsApi, Project } from '@/lib/api'
import { useProjects } from '@/contexts/ProjectsContext'
import { ChevronDown, Search, X, Check, Plus } from 'lucide-react'

interface Props {
  /** The project's public_id (UUID string) or undefined for no selection */
  value: string | undefined
  /** Called with the project's public_id (UUID string) or undefined when cleared */
  onChange: (projectId: string | undefined) => void
  placeholder?: string
  allowNone?: boolean
  includeInternal?: boolean
  status?: string
  style?: React.CSSProperties
  disabled?: boolean
  required?: boolean
  label?: string
  onProjectsLoaded?: (projects: Project[]) => void
}

// Module-level cache with 30s TTL
let cachedProjects: Project[] | null = null
let cachePromise: Promise<Project[]> | null = null
let cacheTimestamp = 0
const CACHE_TTL_MS = 30_000

async function fetchProjects(includeInternal: boolean, status?: string): Promise<Project[]> {
  const useCache = !includeInternal && !status
  const cacheValid = useCache && cachedProjects && (Date.now() - cacheTimestamp < CACHE_TTL_MS)
  if (cacheValid) return cachedProjects!
  if (useCache && cachePromise) return cachePromise

  const doFetch = async () => {
    const res = await projectsApi.list({ limit: 500, ...(status ? { status } : {}) })
    return res.data || []
  }

  if (useCache) {
    cachePromise = doFetch()
    cachedProjects = await cachePromise
    cacheTimestamp = Date.now()
    cachePromise = null
    return cachedProjects
  }
  return doFetch()
}

/** Get the cached project list (null if not yet fetched). Useful for lookups. */
export function getCachedProjects(): Project[] | null {
  return cachedProjects
}

/** Invalidate the project cache. Call after create/edit/delete of a project. */
export function invalidateProjectCache() {
  cachedProjects = null
  cachePromise = null
  cacheTimestamp = 0
}

export const ProjectSelector: React.FC<Props> = ({
  value,
  onChange,
  placeholder = 'Select project...',
  allowNone = false,
  includeInternal = false,
  status,
  style,
  disabled,
  required,
  label,
  onProjectsLoaded,
}) => {
  // Projects now come from the single-source ProjectsContext. The old
  // module-level cache + per-instance fetch is retained below as a
  // status-filtered fallback (rare — most callers don't pass `status`).
  const { projects: ctxProjects, loading: ctxLoading, refresh: refreshProjects } = useProjects()
  const [projects, setProjects] = useState<Project[]>(status ? [] : ctxProjects)
  const [loading, setLoading] = useState<boolean>(status ? true : ctxLoading)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightIdx, setHighlightIdx] = useState(0)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 280 })

  // Sync with context when no status filter is requested.
  useEffect(() => {
    if (status) return
    setProjects(ctxProjects)
    setLoading(ctxLoading)
    if (!ctxLoading) onProjectsLoaded?.(ctxProjects)
    // onProjectsLoaded intentionally excluded — caller may inline it each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxProjects, ctxLoading, status])

  // Status-filtered path: keep the legacy module-level fetch for now.
  useEffect(() => {
    if (!status) return
    let cancelled = false
    fetchProjects(includeInternal, status)
      .then(list => {
        if (cancelled) return
        setProjects(list)
        onProjectsLoaded?.(list)
      })
      .catch(() => { if (!cancelled) setProjects([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [includeInternal, status])

  // Close on outside click — portal-aware: check both trigger and dropdown
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Compute dropdown position from trigger rect
  useLayoutEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 280) })
    }
  }, [open])

  // Focus search when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 10)
      setHighlightIdx(0)
      setSearch('')
      setCreateError('')
    }
  }, [open])

  const selectedProject = useMemo(
    () => projects.find(p => String(p.id) === String(value)),
    [projects, value]
  )

  const filteredProjects = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return projects
    return projects.filter(p => p.name.toLowerCase().includes(q))
  }, [projects, search])

  const options = useMemo(() => {
    const opts: { id: string | null; name: string }[] = []
    if (allowNone && !required) opts.push({ id: null, name: '— None —' })
    for (const p of filteredProjects) opts.push({ id: String(p.id), name: p.name })
    return opts
  }, [filteredProjects, allowNone, required])

  const handleSelect = (projectId: string | null) => {
    onChange(projectId == null ? undefined : projectId)
    setOpen(false)
  }

  // Inline create: makes a project from the typed name, adds it locally for
  // immediate selection, and refreshes the shared ProjectsContext so it appears
  // app-wide (also invalidates the legacy status-filtered module cache).
  const handleCreate = async () => {
    const name = search.trim()
    if (!name || creating) { searchRef.current?.focus(); return }
    setCreating(true)
    setCreateError('')
    try {
      const created = await projectsApi.create({ name })
      setProjects(prev => [created, ...prev.filter(p => String(p.id) !== String(created.id))])
      invalidateProjectCache()
      void refreshProjects()
      onChange(String(created.id))
      setOpen(false)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create project')
    } finally {
      setCreating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIdx(i => Math.min(i + 1, options.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = options[highlightIdx]
      if (opt) handleSelect(opt.id)
      else if (search.trim()) void handleCreate()
    }
  }

  const displayText = loading
    ? 'Loading...'
    : selectedProject?.name || (value ? '(project not in list)' : placeholder)

  const isEmpty = !selectedProject && !value

  // Dropdown rendered via portal to escape overflow:auto/hidden containers
  const dropdown = open ? createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        background: tokens.color.background.elevated,
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: 8,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        zIndex: 10000,
        overflow: 'hidden',
        fontFamily: tokens.typography.fontFamily.sans,
      }}
    >
      {/* Search bar */}
      <div style={{ padding: 8, borderBottom: `1px solid ${tokens.color.border.default}`, position: 'relative' }}>
        <Search size={13} style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', color: tokens.color.text.muted, pointerEvents: 'none' }} />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setHighlightIdx(0) }}
          onKeyDown={handleKeyDown}
          placeholder="Search projects..."
          style={{
            width: '100%',
            padding: '6px 10px 6px 30px',
            background: 'var(--bg-page)',
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: 6,
            color: 'var(--text-primary)',
            fontSize: 13,
            outline: 'none',
            fontFamily: tokens.typography.fontFamily.sans,
            boxSizing: 'border-box',
          }}
        />
        {search && (
          <button
            type="button"
            onClick={() => { setSearch(''); searchRef.current?.focus() }}
            style={{ position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: tokens.color.text.muted, cursor: 'pointer', padding: 2, minHeight: 16, display: 'flex', alignItems: 'center' }}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Options list */}
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {options.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: tokens.color.text.muted, fontSize: 13 }}>
            {loading ? 'Loading...' : 'No projects found'}
          </div>
        )}
        {options.map((opt, idx) => {
          const isSelected = (opt.id == null && !value) || String(opt.id) === String(value)
          const isHighlighted = idx === highlightIdx
          return (
            <button
              key={opt.id ?? 'none'}
              type="button"
              onClick={() => handleSelect(opt.id)}
              onMouseEnter={() => setHighlightIdx(idx)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                width: '100%',
                padding: '8px 14px',
                background: isHighlighted ? 'var(--bg-surface)' : 'transparent',
                border: 'none',
                color: isSelected ? tokens.color.brand.gold : 'var(--text-primary)',
                fontWeight: isSelected ? 600 : 400,
                fontSize: 13,
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: tokens.typography.fontFamily.sans,
                minHeight: 32,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {opt.name}
              </span>
              {isSelected && <Check size={14} style={{ color: tokens.color.brand.gold, flexShrink: 0 }} />}
            </button>
          )
        })}
      </div>

      {/* Footer: count line (preserved) + pinned "+ New Project" create action */}
      <div style={{ borderTop: `1px solid ${tokens.color.border.default}`, background: tokens.color.background.elevated }}>
        {!loading && projects.length > 0 && (
          <div style={{ padding: '6px 14px 0', fontSize: 10, color: tokens.color.text.muted, textAlign: 'right' }}>
            {filteredProjects.length} of {projects.length} projects
          </div>
        )}
        {createError && (
          <div role="alert" style={{ padding: '6px 14px 0', color: tokens.color.feedback.error, fontSize: 11 }}>
            {createError}
          </div>
        )}
        <button
          type="button"
          disabled={creating}
          onClick={() => void handleCreate()}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px',
            background: 'transparent', border: 'none', color: tokens.color.brand.gold,
            cursor: creating ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600,
            fontFamily: tokens.typography.fontFamily.sans, textAlign: 'left', minHeight: 32,
          }}
        >
          <Plus size={14} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {creating ? 'Creating Project…' : search.trim() ? `New Project (“${search.trim()}”)` : 'New Project'}
          </span>
        </button>
      </div>
    </div>,
    document.body,
  ) : null

  return (
    <div style={{ position: 'relative', display: 'inline-block', ...style }}>
      {label && (
        <label style={{ display: 'block', fontSize: 11, color: tokens.color.text.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}{required ? ' *' : ''}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          minWidth: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '7px 10px',
          background: 'var(--bg-elevated)',
          border: `1px solid ${open ? tokens.color.brand.gold : tokens.color.border.default}`,
          borderRadius: 6,
          color: isEmpty ? tokens.color.text.muted : 'var(--text-primary)',
          fontSize: 13,
          fontFamily: tokens.typography.fontFamily.sans,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          minHeight: 32,
          transition: 'border-color 0.1s',
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {displayText}
        </span>
        <ChevronDown size={14} style={{ color: tokens.color.text.muted, flexShrink: 0, transition: 'transform 0.1s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {dropdown}
    </div>
  )
}
